<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { listPersonnelEmployees } from '../productService'
import type { ProductionEmployee } from '../types'

const props = defineProps<{
  open: boolean
  title: string
}>()

const emit = defineEmits<{
  close: []
  select: [employee: ProductionEmployee]
}>()

const client = useApiClient()
const search = ref('')
const employees = ref<ProductionEmployee[]>([])
const loading = ref(false)
const errorMessage = ref('')
const page = ref(1)
const pageSize = 8
const totalPages = computed(() => Math.max(1, Math.ceil(employees.value.length / pageSize)))
const visibleEmployees = computed(() => {
  const start = (page.value - 1) * pageSize
  return employees.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleEmployees.value.map((employee) => ({
    id: employee.id,
    title: `${employee.first_name} ${employee.last_name}`,
    subtitle: employee.job_title
      ? `${employee.employee_code} · ${employee.job_title}`
      : employee.employee_code,
    meta: employee.department_agent_name || `#${employee.id}`,
  })),
)
let searchTimer: ReturnType<typeof setTimeout> | undefined

async function load(): Promise<void> {
  loading.value = true
  errorMessage.value = ''
  try {
    employees.value = await listPersonnelEmployees(client, search.value.trim() || undefined)
    page.value = Math.min(page.value, Math.max(1, Math.ceil(employees.value.length / pageSize)))
  } catch (error: unknown) {
    employees.value = []
    errorMessage.value = formatApiError(error)
  } finally {
    loading.value = false
  }
}

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
    void load()
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  clearTimeout(searchTimer)
  page.value = 1
  searchTimer = setTimeout(load, 300)
})

function selectOption(option: SmartPickerOption): void {
  const employee = visibleEmployees.value.find((entry) => entry.id === Number(option.id))
  if (employee) emit('select', employee)
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="$t('engineering.passportPicker.personnelRepository')"
    :search-label="$t('engineering.passportPicker.searchPersonnel')"
    :search-placeholder="$t('engineering.passportPicker.personnelSearchPlaceholder')"
    :search-value="search"
    :loading="loading"
    :error-message="errorMessage"
    :empty-message="$t('engineering.passportPicker.noPersonnel')"
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="$t('engineering.passportPicker.page', { count: employees.length, page, total: totalPages })"
    :close-aria-label="$t('engineering.passportPicker.closePersonnel')"
    :retry-label="$t('engineering.passportPicker.retry')"
    :previous-label="$t('engineering.passportPicker.previous')"
    :next-label="$t('engineering.passportPicker.next')"
    :cancel-label="$t('engineering.passportPicker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #loading>{{ $t('engineering.passportPicker.loadingPersonnel') }}</template>
  </SmartPickerModal>
</template>
